import React, { useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { state } from '../store/global';
import API from '../lib/api';

import IconButton from '../components/common/IconButton';
import NormalButton from '../components/common/NormalButton';
import Tooltip from '../components/common/Tooltip';
import Table from '../components/common/Table';
import LoadingBar from '../components/common/LoadingBar';
import Pagination from '../components/common/Pagination.jsx';

import PlusIcon from '../assets/images/plus.svg';

import ArrowsLeftRightIcon from '../assets/images/arrowsLeftRight.svg';
import EyeIcon from '../assets/images/eye.svg';
import InfoIcon from '../assets/images/info.svg';
import RewardsIcon from '../components/svg/RewardsIcon';
import TableIcon from '../assets/images/table.svg';

export default function Dashboard() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isWalletCreationModal, setIsWalletCreationModal] = useState(false);
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [balance, setBalance] = useState(0.0);
  const [viewAll, setViewAll] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [totalRewardsMonth, setTotalRewardsMonth] = useState(null);

  const statusConv = {
    'OFF RAMP': 'Transferencia enviada',
    'ON RAMP': 'Transferencia recibida',
    REWARD: 'Recompensa',
    RECHARGE: 'Recarga',
    EXCHANGE: 'Conversión de moneda',
  };

  const rewardsHeader = ['Fecha', 'Hora', 'Movimiento', 'Monto', 'Estado'];
  const formatNumberBR = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };
  useEffect(() => {
    const getWalletBalance = async () => {
      try {
        const res = await API.get('user/getDashboardInfo');

        setBalance(res.data.data.balance);
        setTotalRewardsMonth(res.data.data.totalRewards);
        setIsWalletCreationModal(true);
        setLoading(false);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message;
        console.log('err: ', msg);
        if (msg == 'User not found') navigate('/login');
        else setLoading(false);
      }
    };

    getWalletBalance();
    getTransacitons();
  }, []);

  const onCreateWallet = async () => {
    setIsCreatingWallet(true);
    await API.post('user/createWallet')
      .then((res) => {
        console.log(res);
        setBalance(0.0);
        setIsWalletCreationModal(true);
        setIsCreatingWallet(false);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message;
        console.log(msg);
        setIsCreatingWallet(false);
      });
  };

  const getTransacitons = async () => {
    const res = await API.get(`/transaction/getTransactionsByUser/${snap.user.id}`);

    if (res.data.success === 'success') {
      setTransactions(res.data.data);
    }
  };

  const gotToPage = (path) => {
    navigate(path);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const currentItems = transactions.slice(startIndex, endIndex);

  return (
    <>
      {!loading ? (
        <>
          {isWalletCreationModal ? (
            <div className="px-10 py-8 space-y-8">
              <div className="sm:flex sm:border-b-1 sm:border-b-[#E9EAEB] items-center justify-between">
                <div className="font-[poppins] py-4 pr-2 space-y-1">
                  <p className="text-xl font-semibold text-[#181D27]">
                    ¡Buenos días, {snap.user.firstName + ' ' + snap.user.lastName}!
                  </p>
                  <p className="text-sm font-normal text-[#535862]">
                    Aquí encontraras tu saldo, movimientos y recompensas cuando empieces a utilizar
                    tu cuenta.
                  </p>
                </div>
                <span className="space-x-3 flex">
                  <IconButton
                    bgColor="bg-[#FDFDFD]"
                    icon={PlusIcon}
                    text="Recargar"
                    textColor="text-[#092F7B]"
                    onClick={() => gotToPage('/dashboard/recharge')}
                  />
                  <IconButton
                    bgColor="bg-[#092F7B]"
                    icon={ArrowsLeftRightIcon}
                    text="Transferir"
                    textColor="text-[#FDFDFD]"
                    onClick={() => gotToPage('/dashboard/transfer')}
                  />
                </span>
              </div>

              <div className="sm:flex sm:space-x-6 space-y-4 sm:space-y-0">
                <div className="w-full sm:w-85 p-6 bg-white rounded-[20px] shadow-xs border-1 border-[#E6EBF7] sm:space-y-2">
                  <div className="flex items-center justify-start space-x-1">
                    <p className="text-[#414651] text-base font-semibold">Cuentas</p>
                    <img src={EyeIcon} alt="Eye Icon" className="w-5 h-5" />
                  </div>
                  <div className="p-6 bg-[#F9FAFB] rounded-[20px]">
                    <p className="text-sm text-[#181D27] font-normal">Saldo acumulado</p>
                    <p className="text-[22px] font-semibold">
                      ${formatNumberBR(balance)} <span className="text-lg">USD</span>
                    </p>
                  </div>
                </div>

                <div className="w-full sm:w-78 p-6 bg-white rounded-[20px] shadow-xs border-1 border-[#E6EBF7] space-y-2">
                  <div className="flex items-center justify-start space-x-1">
                    <div className="w-[22px] h-[22px] bg-[#FEF0C7] flex items-center justify-center rounded-full">
                      <RewardsIcon color="#F79009" size="w-[14px] h-[14px]" />
                    </div>
                    <p className="text-[#414651] text-base font-semibold pr-1">Recompensas</p>
                    {/* Tooltip wrapper */}
                    <div className="relative group">
                      <img src={InfoIcon} alt="Info Icon" className="w-5 h-5 cursor-pointer" />
                      <Tooltip text="Las recompensas son variables y no están garantizadas. Dependes del mercado." />
                    </div>
                  </div>
                  {totalRewardsMonth < 0 ? (
                    <div className="min-h-25 bg-[#F9FAFB] rounded-[20px] space-y-2 text-center text-sm px-6 py-3 flex flex-col justify-between items-center">
                      <p className="text-[#717680] font-normal">
                        Destina parte de tu saldo para comenzar a recibirlas
                      </p>
                      <p className="text-[#2A59C6] font-medium">Generar</p>
                    </div>
                  ) : (
                    <div className="p-6 bg-[#F9FAFB] rounded-[20px]">
                      <p className="text-sm text-[#181D27] font-normal">Acumulado del mes</p>
                      <p className="text-[22px] font-semibold">
                        + ${formatNumberBR(totalRewardsMonth)} <span className="text-lg">USD</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-[20px] px-4 py-6 space-y-8">
                <div className="space-x-8 flex items-center justify-between">
                  <p className="text-[16px] font-[600]">Últimos movimientos</p>

                  <div className="px-4.5 py-3 space-x-3 flex items-center justify-center">
                    <button
                      onClick={() => {
                        viewAll ? setViewAll(false) : setViewAll(true);
                      }}
                      className=" float-light text-[14px] font-[500] text-[#092F7B] leading-[22px] font-semibold cursor-pointer"
                    >
                      Ver todas
                    </button>
                  </div>
                </div>

                <div>
                  {currentItems.length == 0 && (
                    <div className="py-6 space-y-2 bg-[#FDFDFD] border-1 border-[#F5F5F5] rounded-[10px]">
                      <div className="flex items-center justify-center">
                        <div className="w-9 h-9 bg-[#E6EBF7] rounded-full flex items-center justify-center">
                          <img
                            src={TableIcon}
                            className="w-5 h-5 bg-[#E6EBF7] rounded-full flex items-center justify-center"
                          />
                        </div>
                      </div>
                      <p className="text-center text-sm font-medium text-[#535862]">
                        Todavía no tienes movimientos registrados
                      </p>
                      <p className="text-center text-sm font-normal text-[#717680]">
                        Aquí verás todas tus recargas, transferencias, conversiones y recompensas
                        una vez comiences a operar.
                      </p>
                    </div>
                  )}

                  {currentItems.length && (
                    <div className="relative overflow-x-auto border-1 border-[#E1E1E1] sm:rounded-lg">
                      <table className="w-full font-[poppins] text-sm text-left rtl:text-right text-[#535862]">
                        <thead className="border-b-1 border-b-[#E1E1E1]">
                          <tr>
                            {rewardsHeader.map((name, idx) => (
                              <th key={idx} scope="col" className="px-5 py-4.5">
                                <div className="flex items-center space-x-3">
                                  <p className="font-normal">{name}</p>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((item, idx) => {
                            return (
                              <tr key={idx} className="border-b-1 border-b-[#E1E1E1]">
                                <td className="p-5 min-w-30">
                                  <p className="text-sm text-[#252B37] leading-[20px]">
                                    {item.created_at.slice(0, 10).replace(/-/g, '/')}
                                  </p>
                                </td>
                                <td className="p-5">
                                  <p className="text-sm text-[#535862] leading-[20px]">
                                    {item.created_at.slice(11, 19)}
                                  </p>
                                </td>
                                <td className="p-2">
                                  <div className="min-w-75">
                                    <div className="flex items-center justify-between">
                                      <p className="text-xs text-[#535862]">
                                        <strong>{statusConv[item.type]}</strong>
                                      </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <p className="text-xs text-[#535862]">{item.description}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="min-w-40 p-5">{`${
                                  item.currency === 'USDC'
                                    ? '$' + formatNumberBR(item.amount)
                                    : item.destination.currency === 'usdc'
                                    ? '$' + formatNumberBR(item.destination.amount)
                                    : item.destination.currency.toUpperCase() +
                                      formatNumberBR(item.destination.amount)
                                }`}</td>

                                <td className={`p-2`}>
                                  <span className="badge d-flex align-items-center rounded-pill px-3 py-2 aprobado">
                                    {item.status !== 'FAILED' ? (
                                      <button className="flex items-center gap-1 px-2 py-1 rounded-[54px] text-xs leading-[20px] bg-green-100 text-green-600 cursor-pointer">
                                        <span className="w-2 h-2 rounded-full bg-green-600"></span>
                                        Aprobado
                                      </button>
                                    ) : (
                                      <button className="flex items-center gap-1 px-2 py-1 rounded-[54px] text-xs leading-[20px] bg-red-100 text-red-600 cursor-pointer">
                                        <span className="w-2 h-2 rounded-full bg-red-600"></span>
                                        Rechazado
                                      </button>
                                    )}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <Pagination
                  total={transactions.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          ) : (
            <div className="relative">
              <div className="fixed z-50 inset-0 bg-black opacity-30 w-full h-full"></div>
              <div className="z-51 fixed inset-0 flex items-center justify-center">
                <div className="font-[poppins] w-91 py-6 bg-white rounded-[20px]">
                  <div className="pb-2 px-6 border-b-1 border-b-[#E9EAEB] flex items-center justify-between">
                    <p className="font-base text-[#252B37] font-medium leading-[20px]">
                      Crear billetera
                    </p>
                    <button
                      onClick={() => setIsWalletCreationModal(true)}
                      className="cursor-pointer"
                      disabled={isCreatingWallet}
                    >
                      <X className="w-6 h-6 text-[#181D27]" />
                    </button>
                  </div>

                  <div className="px-4 py-4 space-y-4">
                    {isCreatingWallet ? (
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="w-12 h-12 border-4 border-[#092F7B] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-sm text-[#535862] font-normal leading-[20px] text-center">
                          Creando tu billetera...
                        </p>
                        <p className="text-sm text-[#717680] font-normal leading-[20px] text-center">
                          Este proceso tardará unos segundos.
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-[#535862] font-normal leading-[20px] text-center">
                          Antes de comenzar, necesitarás una billetera custodiada para tus dólares.
                        </p>
                        <p className="text-sm text-[#535862] font-normal leading-[20px] text-center">
                          Éste proceso tardará unos segundos.
                        </p>
                      </>
                    )}
                  </div>

                  <div className="px-6">
                    <NormalButton
                      customClass={`w-full py-3 ${
                        isCreatingWallet ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#092F7B]'
                      } text-[#F5F5F5]`}
                      text={isCreatingWallet ? 'Procesando...' : 'Crear billetera'}
                      onClick={onCreateWallet}
                      disabled={isCreatingWallet}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <LoadingBar />
      )}
    </>
  );
}
